import { useEffect, useRef, useState } from "react";
import { ClipboardCheck, SearchX } from "lucide-react";
import {
  Button,
  EmptyState,
  ListCard,
  SearchField,
  SkeletonList,
  SortSelect,
  StatusBadge,
  Timestamp,
} from "@/design-system/components/ui";
import {
  prequalSiteApi,
  type PrequalCompletion,
  type PrequalCompletions,
} from "../../api/prequalSite";
import {
  PREQUAL_PAGE_SIZE,
  PREQUAL_SORT_OPTIONS,
  errorMessage,
  figureFor,
  outcomeLabel,
  outcomeTone,
  type CompletionsPanelProps,
  type PrequalSort,
} from "./panelProps";
import { PrequalPager } from "./PrequalPager";

// Partner Brand Ownership — the Completions list.
//
// The drill-in that opens off one of these rows is a SIBLING file now
// (`CompletionDetailDrawer`), mounted by the shell as a drawer alongside this
// panel rather than in place of the whole tab. It used to live here and be
// exported for the shell to render instead of everything, which is why this
// file's row still does nothing but hand an id upward: `onOpen` writes
// `?completion=` and the shell decides what that means.
//
// What this panel is FOR, beyond moving rows out of the old thousand-line tab:
// the completions read has always been paged server-side and the frontend has
// never sent a page. Every partner has been shown the first 25 rows with
// `totalCount` fetched and thrown away — so a site with 200 completions has 175
// that are not merely hard to reach but have no on-screen evidence of existing.
// The pager below is that defect's fix, not a new feature, which is why it ships
// with search and sort rather than after them: a list you cannot page is a list
// you navigate by searching.
//
// Portal-local by the isolation rule: design-system kit + portal-relative
// imports only, and no React Query — plain useState/useEffect loads, as the rest
// of the portal does.

/** Keystrokes are cheap; a paged server query over an unbounded row count is
 *  not. Matches the portal's established search debounce (PreleadsTab). */
const SEARCH_DEBOUNCE_MS = 300;

export function CompletionsPanel({ mineOnly, initial, onOpen }: CompletionsPanelProps) {
  // `rawSearch` is what the box shows; `q` is what the server has been asked.
  // Two states rather than one so typing stays instant while the query behind
  // it moves at most once per pause.
  const [rawSearch, setRawSearch] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<PrequalSort>("newest");
  const [page, setPage] = useState(1);

  const [data, setData] = useState<PrequalCompletions>(initial);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Last-request-wins. Changing a control while a load is in flight leaves two
  // of them running, and without this the slower one repaints the filter the
  // user has already left — the same guard the invite and draft lists carry,
  // and more load-bearing here because three controls can move independently.
  const requestSeq = useRef(0);

  // The scope is the THIRD control that invalidates the current page, and the
  // only one that arrives as a PROP rather than through a handler in this file
  // — which is how it came to be the one that did not reset it. Reconciled
  // during render, the React-sanctioned adjust-state-on-prop-change shape:
  // an effect would work too, but it would fire a doomed page-2 request first
  // and flash an empty list on the way past. DraftsPanel already did this.
  const [prevScope, setPrevScope] = useState(mineOnly);
  if (prevScope !== mineOnly) {
    setPrevScope(mineOnly);
    setPage(1);
  }

  useEffect(() => {
    // Nothing to debounce — including on mount, where this effect otherwise
    // scheduled an unconditional setPage(1) that landed ~300ms in and silently
    // reverted a Next pressed inside that window.
    const trimmed = rawSearch.trim();
    if (trimmed === q) return;
    const timer = window.setTimeout(() => {
      setQ(trimmed);
      // A new search is a new result set; staying on page 4 of the old one
      // would show an empty page and read as "no matches".
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [rawSearch, q]);

  // 🔴 The seeding rule (D6). The shell's mount fetch IS the site resolver — it
  // cannot be removed — and it answers exactly this query: page 1, no search,
  // newest, everyone. So while every control is still sitting on its default,
  // `initial` is not a cached approximation of what the server would say, it is
  // what the server already said, and fetching again would ask the identical
  // question twice on the tab that opens first.
  //
  // Stated as a pure function of the controls rather than a used-once latch, so
  // it is the same answer every render and a partner who tries "Mine" and comes
  // straight back does not pay for a round trip to see the list they were just
  // looking at. Switching tabs unmounts this panel, so a return trip re-seeds
  // the same way.
  const isSeedQuery = page === 1 && q === "" && sort === "newest" && !mineOnly;

  useEffect(() => {
    if (isSeedQuery) {
      setData(initial);
      setError(null);
      setLoading(false);
      // Retire any in-flight request. Coming BACK to the default query is the
      // one case where the seed itself races a fetch, and a late response from
      // the query just abandoned must not repaint over it.
      requestSeq.current += 1;
      return;
    }

    const seq = ++requestSeq.current;
    setLoading(true);
    void (async () => {
      try {
        const result = await prequalSiteApi.completions({
          // Blank is dropped by the client's query builder either way; sending
          // undefined keeps an empty box indistinguishable from an unsent param.
          q: q || undefined,
          sort,
          mineOnly,
          page,
          pageSize: PREQUAL_PAGE_SIZE,
        });
        if (seq !== requestSeq.current) return;
        setData(result);
        setError(null);
      } catch (err) {
        if (seq !== requestSeq.current) return;
        setError(errorMessage(err, "Couldn't load your prequalification completions."));
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    })();
  }, [isSeedQuery, initial, q, sort, mineOnly, page]);

  const rows = data.items;
  const totalCount = data.totalCount;
  const searching = q !== "";

  return (
    <div className="flex flex-col gap-space-3">
      <div className="flex flex-wrap items-center gap-space-2">
        {/* 🔴 No `label` prop. It defaults to "Search", and that default is
            load-bearing: a label matching /email/i would make the send
            wizard's own Email field ambiguous to a by-label query. */}
        <SearchField
          className="min-w-[220px] flex-1"
          value={rawSearch}
          onChange={setRawSearch}
          placeholder="Search by name or email…"
        />
        <SortSelect
          value={sort}
          onChange={(next) => {
            setSort(next as PrequalSort);
            setPage(1);
          }}
          options={PREQUAL_SORT_OPTIONS}
        />
      </div>

      {/* The count the partner came for. Shown unconditionally rather than only
          when the pager is absent: mirroring the pager's own "single page needs
          no footer" rule here would put that decision in two places, which is
          exactly the drift PrequalPager avoids by deriving its pages from the
          same `totalCount`. The two numbers come from one field and answer
          different questions — how many there are, and where you are. */}
      {totalCount > 0 && (
        <p className="m-0 text-label text-gray-mid" data-testid="prequal-completions-total">
          {totalCount === 1 ? "1 completion" : `${totalCount} completions`}
        </p>
      )}

      {/* In-panel, so the site banner and the tab bar above survive a failed
          list load — a partner who mistypes a search should not lose the whole
          tab. The shell's own error path is for the site read, which is a
          different failure. */}
      {error ? (
        <p className="m-0 text-small text-status-error" role="alert">
          {error}
        </p>
      ) : loading ? (
        <SkeletonList rows={3} data-testid="prequal-completions-loading" />
      ) : rows.length === 0 ? (
        searching ? (
          <EmptyState
            variant="noResults"
            icon={SearchX}
            title="No completions match your search"
            // Names the boundary rather than leaving the partner to guess it.
            // A reference number is the most obvious thing to paste in here and
            // the one thing search deliberately does not look at, so saying so
            // is the difference between a dead end and a next step.
            description="Search looks at the applicant's name and email address. Reference numbers aren't searchable — try the person's name instead."
          />
        ) : (
          <EmptyState
            icon={ClipboardCheck}
            title={mineOnly ? "None of your invitations have been completed yet" : "No completions yet"}
            description={
              mineOnly
                ? "Once someone you invited finishes their prequalification, they'll appear here. Switch to everyone on this site to see the ones that came in on their own."
                : "As people finish a prequalification on your site, each one appears here with their result and reference number."
            }
          />
        )
      ) : (
        <>
          <div className="flex flex-col gap-space-3">
            {rows.map((row) => (
              <CompletionRow key={row.id} row={row} onOpen={() => onOpen(row.id)} />
            ))}
          </div>
          <PrequalPager
            page={data.page}
            pageSize={data.pageSize}
            totalCount={totalCount}
            onPage={setPage}
          />
        </>
      )}
    </div>
  );
}

function CompletionRow({ row, onOpen }: { row: PrequalCompletion; onOpen: () => void }) {
  const figure = figureFor(row);
  return (
    <ListCard
      data-testid={`prequal-row-${row.id}`}
      title={`${row.firstName} ${row.lastName}`}
      status={<StatusBadge tone={outcomeTone(row.outcome)}>{outcomeLabel(row.outcome)}</StatusBadge>}
      meta={
        <span className="flex flex-wrap items-center gap-x-space-2 gap-y-space-1">
          <span>{row.role}</span>
          {figure && (
            <>
              <span aria-hidden>·</span>
              <span>{figure}</span>
            </>
          )}
          {row.referenceNumber && (
            <>
              <span aria-hidden>·</span>
              <span>{row.referenceNumber}</span>
            </>
          )}
          <span aria-hidden>·</span>
          <span>
            <Timestamp value={row.completedAtUtc} />
          </span>
        </span>
      }
      actions={
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onOpen}
          data-testid={`prequal-open-${row.id}`}
        >
          View
        </Button>
      }
    />
  );
}
